import React, { useState, useEffect } from 'react';
import './App.css';
import Dashboard from './components/Dashboard';
import Auth from './components/Auth';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import { auth } from './firebase';
import { onAuthStateChanged, signOut, getRedirectResult } from "firebase/auth";
import { bibleApi } from './api';
import logo from './logo.svg';

function App() {
  const [isLightMode, setIsLightMode] = useState(false);
  const [currentView, setCurrentView] = useState('landing');
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    if (!auth) {
      console.error("Firebase Auth instance not found.");
      return;
    }

    // Handle Google redirect result (fires once after returning from Google OAuth)
    getRedirectResult(auth).then((result) => {
      if (result?.user) {
        navigateToDashboard();
      }
    }).catch((err) => {
      console.error("Google redirect sign-in error:", err.message);
    });

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const response = await bibleApi.syncUser();
          setUserProfile(response.profile);
          console.log('[PlutoBible] subscription status:', response.profile?.is_subscribed);
        } catch (err) {
          console.error("Failed to sync user with backend:", err);
        }
      } else {
        setUserProfile(null);
      }
    });

    // Re-sync profile when tab regains focus (e.g. user returns from Stripe payment page)
    const handleFocus = async () => {
      if (!auth?.currentUser) return;
      try {
        const response = await bibleApi.syncUser();
        setUserProfile(response.profile);
      } catch (err) {
        console.error("Failed to refresh user profile:", err);
      }
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      unsubscribe();
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const toggleTheme = () => setIsLightMode(!isLightMode);
  const navigateToDashboard = () => setCurrentView('dashboard');
  const navigateToLanding = () => setCurrentView('landing');
  const navigateToTerms = () => setCurrentView('terms');
  const navigateToPrivacy = () => setCurrentView('privacy');

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigateToLanding();
    } catch (error) {
      console.error("Logout Error", error);
    }
  };

  const handleCTAClick = () => {
    navigateToDashboard();
  };

  const handleAuthClose = () => {
    setShowAuth(false);
    if (auth?.currentUser) {
      navigateToDashboard();
    }
  };

  if (currentView === 'terms') {
    return (
      <div className={`App ${isLightMode ? 'light-mode' : ''}`}>
        <TermsOfService onBack={navigateToLanding} />
      </div>
    );
  }

  if (currentView === 'privacy') {
    return (
      <div className={`App ${isLightMode ? 'light-mode' : ''}`}>
        <PrivacyPolicy onBack={navigateToLanding} />
      </div>
    );
  }

  if (currentView === 'dashboard') {
    return (
      <Dashboard
        onNavigateHome={navigateToLanding}
        isLightMode={isLightMode}
        onToggleTheme={toggleTheme}
        user={user}
        userProfile={userProfile}
        isSubscribed={userProfile?.is_subscribed ?? false}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div className={`App ${isLightMode ? 'light-mode' : ''}`}>
      {showAuth && <Auth onClose={handleAuthClose} />}

      {/* Navbar */}
      <nav className="Navbar">
        <span className="Nav-brand">
          <img src={logo} alt="PlutoBible" className="Nav-logo" />
          PlutoBible
        </span>
        <div className="Nav-actions">
          <button className="Theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {isLightMode ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            )}
          </button>
          {user ? (
            <button className="Nav-cta" onClick={navigateToDashboard}>Open Bible</button>
          ) : (
            <button className="Nav-cta" onClick={() => setShowAuth(true)}>Sign In</button>
          )}
        </div>
      </nav>

      {/* Hero */}
      <header className="Hero">
        <div className="Hero-content">
          <p className="Hero-eyebrow">Your AI-powered Bible companion</p>
          <h1 className="Hero-title">
            Study the Word<br />Like Never Before
          </h1>
          <p className="Hero-subtitle">
            PlutoBible combines the timeless wisdom of Scripture with modern AI to help you read, understand, and reflect — at your own pace.
          </p>
          <button className="CTA-button" onClick={handleCTAClick}>
            {user ? 'Open Your Bible' : 'Read the Bible'}
          </button>
        </div>
      </header>

      {/* Bible Verse */}
      <section className="Verse-section">
        <div className="Verse-card">
          <div className="Quote-mark">&ldquo;</div>
          <blockquote className="Verse-text">
            Your word is a lamp to my feet and a light to my path.
          </blockquote>
          <cite className="Verse-ref">Psalm 119:105</cite>
        </div>
      </section>

      {/* Features */}
      <section className="Features-section">
        <h2 className="Section-title">Everything you need to go deeper</h2>
        <div className="Features-grid">
          <div className="Feature-card">
            <div className="Feature-icon">✦</div>
            <h3>AI Bible Assistant</h3>
            <p>
              Ask any question about Scripture and receive thoughtful, context-aware answers powered by AI and your own study history.
            </p>
          </div>
          <div className="Feature-card">
            <div className="Feature-icon">✎</div>
            <h3>Personal Study Notes</h3>
            <p>
              Write reflections and annotations directly alongside the text. Your thoughts, your journey — always saved and private.
            </p>
          </div>
          <div className="Feature-card">
            <div className="Feature-icon">✧</div>
            <h3>Community Questions</h3>
            <p>
              Engage with fellow believers, share insights, and learn from diverse perspectives on faith and Scripture.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="Bottom-CTA">
        <h2>Ready to begin?</h2>
        <p>Join a growing community of believers deepening their faith through Scripture.</p>
        <button className="CTA-button" onClick={handleCTAClick}>
          {user ? 'Open Your Bible' : 'Start Reading'}
        </button>
      </section>

      <footer className="Footer">
        <a
          className="Support-link"
          href="https://buy.stripe.com/test_3cI7sEbMI25w3gicTk1kA00"
          target="_blank"
          rel="noopener noreferrer"
        >
          ♥ Support PlutoBible
        </a>
        <p>&copy; {new Date().getFullYear()} PlutoBible. Built for spiritual growth.</p>
        <p className="Footer-company">A product of BytePluto Inc.</p>
        <div className="Footer-legal">
          <button className="Footer-legal-link" onClick={navigateToTerms}>Terms of Service</button>
          <button className="Footer-legal-link" onClick={navigateToPrivacy}>Privacy Policy</button>
        </div>
      </footer>
    </div>
  );
}

export default App;
